import {
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_KLARNA_AUTHORIZED,
  ORDER_STATUS_PAID,
  ORDER_STATUS_RETURNED,
  ORDER_STATUS_SHIPPED,
} from "../constants/OrderConstant";
import { CARRIER_OPTIMOROUTE, CARRIER_SOOCOOL, CARRIER_TRUNKRS, getCarrierBooking } from "./carrierBooking";

/**
 * "Vervoerder loskoppelen" (client request 2026-10-19): take an order away from the carrier it was
 * booked with and put it back to paid, so it shows up in Nog te doen again and can be booked anew,
 * with the same carrier or another one.
 *
 * | Carrier     | What happens                                                                 |
 * |-------------|------------------------------------------------------------------------------|
 * | SooCool     | cancelled at SooCool through the api, which also removes the `soocool` block |
 * | Trunkrs     | the CMS clears `trunkrsNumber`; staff remove the shipment in Trunkrs itself  |
 * | OptimoRoute | the CMS clears `optimoRouteExportedAt`; staff remove the stop in OptimoRoute |
 *
 * Then the CMS puts the status back to what the payment webhook wrote before the export: PAID, or
 * KLARNA_AUTHORIZED for Klarna. A delivered (COMPLETED) or returned order stays as it is.
 *
 * Markers are cleared to null rather than deleted: both the CMS (`getCarrierBooking`) and the api
 * (`FirestoreService.getOrderCarrierState`) read null as "no booking", and the cached order in
 * `useOrders` gets a real value instead of a delete sentinel until the realtime update arrives.
 *
 * Pure: no Firestore, no React.
 */

/** Same ids as BNPL_PAYMENT_METHOD_IDS in PayService (api/ and halalonline/api): Klarna, Klarna new, Klarna sandbox. */
export const KLARNA_PAYMENT_METHOD_IDS = ["1717", "2265", "613"];

export const isKlarnaOrder = (order) =>
  KLARNA_PAYMENT_METHOD_IDS.includes(String(order?.paymentMethodId ?? "")) ||
  /klarna/i.test(order?.paymentMethodName || "");

/** The status the payment webhook gave the order, before any carrier set it to SHIPPED. */
export const statusBeforeShipping = (order) =>
  isKlarnaOrder(order) ? ORDER_STATUS_KLARNA_AUTHORIZED : ORDER_STATUS_PAID;

/** Only SHIPPED goes back; any other status (PAID already, CREATED, ...) is left alone. */
export const statusAfterRelease = (order) =>
  order?.orderStatus === ORDER_STATUS_SHIPPED ? statusBeforeShipping(order) : order?.orderStatus;

export const RELEASE_SKIP_NO_CARRIER = "no_carrier";
export const RELEASE_SKIP_DELIVERED = "delivered";
export const RELEASE_SKIP_RETURNED = "returned";

/** Why an order cannot be released, or null when it can. */
export const releaseBlockedReason = (order) => {
  if (!getCarrierBooking(order)) return RELEASE_SKIP_NO_CARRIER;
  if (order?.orderStatus === ORDER_STATUS_COMPLETED) return RELEASE_SKIP_DELIVERED;
  if (order?.orderStatus === ORDER_STATUS_RETURNED) return RELEASE_SKIP_RETURNED;
  return null;
};

export const canReleaseCarrier = (order) => releaseBlockedReason(order) === null;

/**
 * What a release of these orders will do, before anything is sent.
 *
 * - `viaSooCool`: cancelled through the api; `reference` is SooCool's own order id.
 * - `manual`: Trunkrs and OptimoRoute; `reference` is the Trunkrs number, if any. The CMS only
 *   clears its own marker, the carrier still has the shipment until staff remove it.
 * - `skipped`: `{ order, reason }` for orders that cannot be released.
 */
export const planCarrierRelease = (orders) => {
  const viaSooCool = [];
  const manual = [];
  const skipped = [];

  (orders || []).forEach((order) => {
    const reason = releaseBlockedReason(order);
    if (reason) {
      skipped.push({ order, reason });
      return;
    }
    const carrier = getCarrierBooking(order);
    const item = { order, carrier, statusAfter: statusAfterRelease(order) };
    if (carrier === CARRIER_SOOCOOL) viaSooCool.push({ ...item, reference: order.soocool.orderId });
    else manual.push({ ...item, reference: carrier === CARRIER_TRUNKRS ? order.trunkrsNumber : null });
  });

  return { viaSooCool, manual, skipped };
};

/**
 * The fields the CMS writes for a release. Never the `soocool` block: the api removes that itself
 * once SooCool has accepted the cancellation, and the rules do not let the CMS write it.
 */
export const releaseUpdates = (order, carrier) => {
  const updates = {};
  if (carrier === CARRIER_TRUNKRS && order?.trunkrsNumber) updates.trunkrsNumber = null;
  if (carrier === CARRIER_OPTIMOROUTE && order?.optimoRouteExportedAt) updates.optimoRouteExportedAt = null;
  const status = statusAfterRelease(order);
  if (status !== order?.orderStatus) updates.orderStatus = status;
  return updates;
};
